import { useState } from "react";
import { Button, Form, Row, Col, FloatingLabel, Spinner, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SendVoucherRequest } from "../../models/voucher";
import { VoucherStore } from "../../stores/VoucherStore";

type Props = {
    store: VoucherStore
}

const UseVoucherForm: React.FC<Props> = ({ store }: Props) => {
    const { t } = useTranslation('voucher');
    const { register, formState: {errors}, handleSubmit, getValues } = useForm();
    const [isLoading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const sendVoucher = async (data: SendVoucherRequest) => {
        setLoading(true);
        await store.sendVoucher(data)
        setModalMessage(() => {
            if (store.useVoucherResult.code === 'err-voucher-already-exist-for-current-mail') {
                return 'Esiste già un voucher per questa email';
            }
            return store.useVoucherResult.message;
        });
        setLoading(false);
        setOpenModal(true);
    }
    return (   
        <>
        <Form className="voucher-form"  onSubmit={handleSubmit((data) => sendVoucher(data as SendVoucherRequest))}>
            <FloatingLabel
                controlId="mail"
                label="E-MAIL"
                className="mb-3"
            >
                <Form.Control
                {...register('mail', {
                    required: "Email richiesta",
                    pattern: {
                        value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                        message: "Email non valida"
                    }
                })} type="email" placeholder="Enter email"  />
                <p className="form-error">{errors.mail?.message as String}</p>
            </FloatingLabel>   
            <FloatingLabel
                controlId="firstName"
                label="NOME"
                className="mb-3"
            >
                <Form.Control
                    {...register('firstName', {
                        required: "Il nome è richiesto"
                    })} 
                    type="text" 
                    placeholder="Enter your name" 
                />
                <p className="form-error">{errors.firstName?.message as String}</p>
            </FloatingLabel>   
            <FloatingLabel
                controlId="lastName"
                label="COGNOME"
                className="mb-3"
            >
                <Form.Control
                    {...register('lastName', {
                        required: "Il cognome è richiesto"
                    })} 
                    type="text" 
                    placeholder="Enter your surname" 
                />
                <p className="form-error">{errors.lastName?.message as String}</p>
            </FloatingLabel>   
            <FloatingLabel
                controlId="phone"
                label="CELLULARE"
                className="mb-3"
            >
                <Form.Control
                    {...register('phone')} 
                    type="text" 
                    placeholder="Cellulare" 
                />
                <p className="form-error">{errors.phone?.message as String}</p>
            </FloatingLabel>
            <FloatingLabel
                controlId="type"
                label="TIPO"
                className="mb-3"
            >
                <Form.Select
                    {...register('type', {
                        required: "Il Tipo è richiesto",
                        valueAsNumber: true
                    })}
                >
                <option value="0">Cliente</option>
                <option value="1">Prospect</option>
              </Form.Select>
                <p className="form-error">{errors.type?.message as String}</p>
            </FloatingLabel>
            <FloatingLabel
                controlId="ndg"
                label="NDG"
                className="mb-3"
            >
                <Form.Control
                    {...register('ndg', {
                        validate: {
                            required: value => {
                                if (getValues('type') === 0 && !value) return "Inserire un ndg";
                                return true;
                            }
                        }
                    })} 
                    type="text" 
                    placeholder="Ndg" 
                />
                <p className="form-error">{errors.ndg?.message as String}</p>
            </FloatingLabel>
            <FloatingLabel
                controlId="companyName"
                label="RAGIONE SOCIALE"
                className="mb-3"
            >
                <Form.Control
                    {...register('companyName', {
                        required: "Il campo è richiesto"
                    })}
                    type="text" 
                    placeholder="Ragione sociale" 
                />
                <p className="form-error">{errors.companyName?.message as String}</p>
            </FloatingLabel>
            <FloatingLabel
                controlId="vatCode"
                    label="PARTITA IVA"
                className="mb-3"
            >
                <Form.Control
                {...register('vatCode', {
                    validate: {
                        required: value => {
                            if (!getValues('taxCode') && !value) return "Inserire un valore in Codice Fiscale o Partita Iva";
                            return true;
                        }
                    }
                })} type="text" placeholder="Partita Iva" />
                <p className="form-error">{errors.vatCode?.message as String}</p>
            </FloatingLabel>
            <FloatingLabel
                controlId="taxCode"
                label="CODICE FISCALE"
                className="mb-3"
            >
                <Form.Control
                {...register('taxCode', {
                    validate: {
                        required: value => {
                            if (!getValues('vatCode') && !value) return "Inserire un valore in Codice Fiscale o Partita Iva";
                            return true;
                        }
                    }
                })} type="text" placeholder="Codice Fiscale" />
                <p className="form-error">{errors.taxCode?.message as String}</p>
            </FloatingLabel>
            <Row className="mt-4">
                <Col md={12} className="text-center px-3 mb-1">
                    <Button  type="submit" variant="primary" className="w-100" disabled={isLoading}>
                        {isLoading && <Spinner animation="grow" size="sm" />} {t("sendVoucher")}
                    </Button>
                </Col>
            </Row>
        </Form>
        <Modal
            key="use-voucher-modal"
            show={openModal}
            onHide={() => setOpenModal(false)}
            className={store.useVoucherResult.status === 'OK' ? 'modal-result' : 'modal-result-error'}
        >
                <Modal.Header closeButton>
                <Modal.Title>Utilizza Voucher</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                <h4>{store.useVoucherResult.status === 'OK' ? 'Voucher inviato con successo!' : 'Errore'}</h4>
                <p>{modalMessage}</p>
                </Modal.Body>

                {store.useVoucherResult.status === 'OK' && (<Modal.Footer>
                <Button variant="primary" onClick={() => setOpenModal(false)}>Ok</Button>
                </Modal.Footer>)}
        </Modal>
      </>
    );
}

export default UseVoucherForm;